"use client";
import Input from "@/components/form/input/InputField";
import Label from "@/components/form/Label";
import Button from "@/components/ui/button/Button";
import { ChevronLeftIcon, EyeCloseIcon, EyeIcon } from "@/icons";
import Link from "next/link";
import React, { useState } from "react";
import { authApi } from "@/api/auth";
import { ROUTES, getEntryPath } from "@/config/routes";

/**
 * Landing page for the link InviteStoreOwnerHandler emails (Admin:BaseUrl + /accept-invite
 * ?token=...). Unlike ResetPasswordForm, acceptStoreInvite returns a real token pair (the
 * store owner's brand-new account, role=admin for their tenant only) — the user is signed in
 * the moment this succeeds, so this redirects into the app rather than back to sign-in.
 */
export default function AcceptInviteForm() {
  const token = typeof window !== "undefined" ? new URLSearchParams(window.location.search).get("token") : null;

  const [showPassword, setShowPassword] = useState(false);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password.length < 10) {
      setError("Password must be at least 10 characters");
      return;
    }
    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }
    if (!token) {
      setError("This invite link is missing or invalid. Ask the platform admin to re-send it.");
      return;
    }
    setError("");
    setIsLoading(true);
    try {
      // AcceptStoreInviteHandler always grants role=admin for the invite's own tenant only
      // (see StoreInviteFlows.cs) — the entry path for a brand-new store owner is always the
      // regular admin shell, never a decode-the-token branch.
      await authApi.acceptStoreInvite(token, password);
      setSuccess(true);
      // Full page reload (not router navigate) so the freshly-set token is picked up by
      // every provider on boot — same reasoning SignInForm's own post-login redirect uses.
      setTimeout(() => {
        window.location.href = getEntryPath("admin");
      }, 1500);
    } catch {
      setError("This invite is invalid or has expired. Ask the platform admin to re-send it.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex flex-col flex-1 lg:w-1/2 w-full">
      <div className="w-full max-w-md sm:pt-10 mx-auto mb-5">
        <Link
          href={ROUTES.SIGNIN.path}
          className="inline-flex items-center text-sm text-gray-500 transition-colors hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
        >
          <ChevronLeftIcon />
          Back to sign in
        </Link>
      </div>
      <div className="flex flex-col justify-center flex-1 w-full max-w-md mx-auto">
        <div>
          <div className="mb-5 sm:mb-8">
            <h1 className="mb-2 font-semibold text-gray-800 text-title-sm dark:text-white/90 sm:text-title-md">
              Set up your store
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Choose a password to activate your account and sign in.
            </p>
          </div>

          {success ? (
            <div className="rounded-lg bg-success-50 p-4 text-sm text-success-700 dark:bg-success-500/10 dark:text-success-400">
              Account activated. Signing you in...
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              <div className="space-y-6">
                {!token && (
                  <p className="text-sm text-error-500">
                    This link is missing its invite token. Ask the platform admin to re-send your invite.
                  </p>
                )}
                <div>
                  <Label htmlFor="password">
                    Password <span className="text-error-500">*</span>
                  </Label>
                  <div className="relative">
                    <Input
                      id="password"
                      name="password"
                      type={showPassword ? "text" : "password"}
                      placeholder="Choose a password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                    />
                    <span
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute z-30 -translate-y-1/2 cursor-pointer right-4 top-1/2"
                    >
                      {showPassword ? (
                        <EyeIcon className="fill-gray-500 dark:fill-gray-400" />
                      ) : (
                        <EyeCloseIcon className="fill-gray-500 dark:fill-gray-400" />
                      )}
                    </span>
                  </div>
                </div>

                <div>
                  <Label htmlFor="confirmPassword">
                    Confirm password <span className="text-error-500">*</span>
                  </Label>
                  <Input
                    id="confirmPassword"
                    name="confirmPassword"
                    type={showPassword ? "text" : "password"}
                    placeholder="Re-enter your password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                  />
                </div>

                {error && <p className="text-sm text-error-500">{error}</p>}

                <div>
                  <Button type="submit" className="w-full" size="sm" disabled={isLoading}>
                    {isLoading ? "Activating..." : "Activate account"}
                  </Button>
                </div>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
